// src/pages/LossOfPay.tsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Button,
  Badge,
  Alert,
  Spinner,
  Input,
  Label,
  FormGroup,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Table,
} from 'reactstrap';
import {
  RefreshCw,
  Search,
  X,
  Edit2,
  Trash2,
  AlertCircle,
  User,
  Calendar,
  ChevronLeft,
  ChevronRight,
  FileText,
} from 'react-feather';
import BreadCrumb from '../../Components/Common/BreadCrumb';
import { toast } from 'react-toastify';
import { apiClient } from '../../helpers/api_helper';
import { format } from 'date-fns';

// ─────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────
interface LossOfPayItem {
  id: number;
  emp_id: number;
  date: string;
  notes: string;
  status: boolean;
  employee?: {
    id: number;
    name: string;
    employee_code: string;
    official_email: string | null;
  } | null;
}

interface PaginationMeta {
  total: number;
  page: number;
  limit: number;
  total_pages: number;
  has_next: boolean;
  has_prev: boolean;
}

interface ListResponse {
  success: boolean;
  message: string;
  data: LossOfPayItem[];
  pagination: PaginationMeta;
}

// ─────────────────────────────────────────────────────────────
// Date helpers
// ─────────────────────────────────────────────────────────────
const formatDisplayDate = (dateStr: string): string => {
  try {
    return format(new Date(dateStr), 'dd MMM yyyy');
  } catch {
    return dateStr;
  }
};

const formatDisplayDateTime = (dateStr: string): string => {
  try {
    return format(new Date(dateStr), 'dd MMM yyyy, hh:mm a');
  } catch {
    return dateStr;
  }
};

// ─────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────
const LossOfPay: React.FC = () => {
  // ── Data ──
  const [records, setRecords] = useState<LossOfPayItem[]>([]);
  const [pagination, setPagination] = useState<PaginationMeta | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // ── Filters ──
  const [filters, setFilters] = useState({
    emp_id: '' as string | number,
    from_date: '',
    to_date: '',
    status: 'true' as 'true' | 'false' | 'all',
    search: '',
  });

  // Applied filters (only used when user clicks "Apply")
  const [appliedFilters, setAppliedFilters] = useState({ ...filters });

  // ── Pagination ──
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [sortBy, setSortBy] = useState<'date' | 'id'>('date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // ── Edit Modal ──
  const [editModal, setEditModal] = useState(false);
  const [editing, setEditing] = useState<LossOfPayItem | null>(null);
  const [editNotes, setEditNotes] = useState('');
  const [editDate, setEditDate] = useState('');
  const [saving, setSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  // ─────────────────────────────────────────────────────────────
  // Fetch
  // ─────────────────────────────────────────────────────────────
  const fetchRecords = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const params: Record<string, any> = {
        page,
        limit,
        sort_by: sortBy,
        sort_order: sortOrder,
      };

      if (appliedFilters.emp_id) params.emp_id = appliedFilters.emp_id;
      if (appliedFilters.from_date) params.from_date = appliedFilters.from_date;
      if (appliedFilters.to_date) params.to_date = appliedFilters.to_date;
      if (appliedFilters.search) params.search = appliedFilters.search;

      // Only send status if the user picked a specific value
      if (appliedFilters.status === 'true') params.status = true;
      else if (appliedFilters.status === 'false') params.status = false;
      // 'all' → don't send status → backend returns both (adjust if needed)

      const response: any = await apiClient.get('/loss-of-pays', params);
      console.log('response ',response)
      // apiClient already unwraps to response.data
      const payload: ListResponse = response;

      if (payload?.success === false) {
        setError(payload.message || 'Failed to fetch Loss of Pay records');
        setRecords([]);
        setPagination(null);
        return;
      }

      setRecords(payload.data || []);
      setPagination(payload.pagination || null);
    } catch (err: any) {
      console.error('Error fetching LOP records:', err);
      setError(err?.message || 'Failed to fetch Loss of Pay records');
      setRecords([]);
      setPagination(null);
    } finally {
      setLoading(false);
    }
  }, [page, limit, sortBy, sortOrder, appliedFilters]);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  // ─────────────────────────────────────────────────────────────
  // Filter handlers
  // ─────────────────────────────────────────────────────────────
  const handleFilterChange = (key: string, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleApply = () => {
    setPage(1);
    setAppliedFilters({ ...filters });
  };

  const handleClear = () => {
    const empty = {
      emp_id: '' as string | number,
      from_date: '',
      to_date: '',
      status: 'true' as 'true' | 'false' | 'all',
      search: '',
    };
    setFilters(empty);
    setAppliedFilters(empty);
    setPage(1);
  };

  const handleSort = (column: 'date' | 'id') => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('desc');
    }
    setPage(1);
  };

  // ─────────────────────────────────────────────────────────────
  // Edit
  // ─────────────────────────────────────────────────────────────
  const openEdit = (record: LossOfPayItem) => {
    setEditing(record);
    setEditNotes(record.notes);
    // yyyy-MM-dd for <input type="date">
    try {
      setEditDate(format(new Date(record.date), 'yyyy-MM-dd'));
    } catch {
      setEditDate('');
    }
    setEditError(null);
    setEditModal(true);
  };

  const handleSaveEdit = async () => {
    if (!editing) return;
    if (!editNotes.trim()) {
      setEditError('Notes are required');
      return;
    }

    setSaving(true);
    setEditError(null);

    try {
      await apiClient.put(`/loss-of-pays/${editing.id}`, {
        notes: editNotes.trim(),
        ...(editDate && { date: editDate }),
      });

      toast.success('Loss of Pay updated');
      setEditModal(false);
      setEditing(null);
      fetchRecords();
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        err?.data?.message ||
        err?.message ||
        'Failed to update';
      setEditError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  // ─────────────────────────────────────────────────────────────
  // Deactivate / Reactivate
  // ─────────────────────────────────────────────────────────────
  const handleToggleStatus = async (record: LossOfPayItem) => {
    const isActive = record.status;
    const confirmMsg = isActive
      ? `Remove Loss of Pay for ${record.employee?.name || 'this employee'} on ${formatDisplayDate(
          record.date
        )}?\n\nSalary will no longer be deducted for this day.`
      : `Reactivate Loss of Pay for ${
          record.employee?.name || 'this employee'
        } on ${formatDisplayDate(record.date)}?`;

    if (!window.confirm(confirmMsg)) return;

    try {
      await apiClient.patch(`/loss-of-pays/${record.id}/status`, {
        status: !isActive,
      });
      toast.success(
        isActive ? 'Loss of Pay removed' : 'Loss of Pay reactivated'
      );
      fetchRecords();
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message ||
          err?.data?.message ||
          err?.message ||
          'Failed to update status'
      );
    }
  };

  // ─────────────────────────────────────────────────────────────
  // Pagination
  // ─────────────────────────────────────────────────────────────
  const goToPage = (newPage: number) => {
    if (!pagination) return;
    if (newPage < 1 || newPage > pagination.total_pages) return;
    setPage(newPage);
  };

  // ─────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────
  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title="Loss of Pay" pageTitle="Loss of Pay" />

        <Row>
          <Col lg={12}>
            <Card>
              <CardHeader className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                <div>
                  <h5 className="card-title mb-0 d-flex align-items-center gap-2">
                    <AlertCircle size={18} style={{ color: '#8b0000' }} />
                    Loss of Pay
                  </h5>
                  {pagination && (
                    <small className="text-muted">
                      Total:{' '}
                      <Badge
                        style={{ backgroundColor: '#8b0000' }}
                        className="ms-1"
                      >
                        {pagination.total}
                      </Badge>{' '}
                      records
                    </small>
                  )}
                </div>
                <Button
                  color="secondary"
                  size="sm"
                  onClick={fetchRecords}
                  disabled={loading}
                >
                  <RefreshCw size={14} className="me-1" />
                  Refresh
                </Button>
              </CardHeader>

              <CardBody>
                {/* ───────────── Filters ───────────── */}
                <div className="mb-3 p-3 border rounded bg-light">
                  <Row className="g-3 align-items-end">
                    <Col md={2}>
                      <FormGroup className="mb-0">
                        <Label className="form-label-sm">From Date</Label>
                        <Input
                          type="date"
                          bsSize="sm"
                          value={filters.from_date}
                          onChange={e =>
                            handleFilterChange('from_date', e.target.value)
                          }
                        />
                      </FormGroup>
                    </Col>
                    <Col md={2}>
                      <FormGroup className="mb-0">
                        <Label className="form-label-sm">To Date</Label>
                        <Input
                          type="date"
                          bsSize="sm"
                          value={filters.to_date}
                          onChange={e =>
                            handleFilterChange('to_date', e.target.value)
                          }
                        />
                      </FormGroup>
                    </Col>
                    <Col md={2}>
                      <FormGroup className="mb-0">
                        <Label className="form-label-sm">Status</Label>
                        <Input
                          type="select"
                          bsSize="sm"
                          value={filters.status}
                          onChange={e =>
                            handleFilterChange('status', e.target.value)
                          }
                        >
                          <option value="true">Active</option>
                          <option value="false">Removed</option>
                          <option value="all">All</option>
                        </Input>
                      </FormGroup>
                    </Col>
                    <Col md={3}>
                      <FormGroup className="mb-0">
                        <Label className="form-label-sm">Search in Reason</Label>
                        <Input
                          type="text"
                          bsSize="sm"
                          placeholder="e.g. misconduct"
                          value={filters.search}
                          onChange={e =>
                            handleFilterChange('search', e.target.value)
                          }
                        />
                      </FormGroup>
                    </Col>
                    <Col md={3}>
                      <div className="d-flex gap-2">
                        <Button
                          color="light"
                          size="sm"
                          onClick={handleClear}
                          className="flex-fill"
                        >
                          <X size={14} className="me-1" />
                          Clear
                        </Button>
                        <Button
                          color="primary"
                          size="sm"
                          onClick={handleApply}
                          className="flex-fill"
                        >
                          <Search size={14} className="me-1" />
                          Apply
                        </Button>
                      </div>
                    </Col>
                  </Row>
                </div>

                {/* ───────────── Error ───────────── */}
                {error && (
                  <Alert color="danger" className="mb-3">
                    <AlertCircle size={16} className="me-2" />
                    {error}
                  </Alert>
                )}

                {/* ───────────── Table ───────────── */}
                {loading ? (
                  <div className="text-center py-5">
                    <Spinner color="primary" className="me-2" />
                    <span>Loading records...</span>
                  </div>
                ) : records.length === 0 ? (
                  <div className="text-center py-5">
                    <AlertCircle
                      size={48}
                      className="text-muted mb-3"
                    />
                    <h5>No Loss of Pay records found</h5>
                    <p className="text-muted mb-0">
                      Try changing the filters above.
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="table-responsive">
                      <Table
                        bordered
                        hover
                        size="sm"
                        className="mb-0 align-middle"
                      >
                        <thead className="table-light">
                          <tr>
                            <th style={{ width: '55px' }} className="text-center">
                              #
                            </th>
                            <th style={{ width: '110px' }}>Employee</th>
                            <th style={{ width: '180px' }}>Name</th>
                            <th
                              style={{ width: '140px', cursor: 'pointer' }}
                              onClick={() => handleSort('date')}
                            >
                              <div className="d-flex align-items-center gap-1">
                                Date
                                {sortBy === 'date' && (
                                  <span className="text-primary">
                                    {sortOrder === 'asc' ? '▲' : '▼'}
                                  </span>
                                )}
                              </div>
                            </th>
                            <th>Reason</th>
                            <th style={{ width: '110px' }} className="text-center">
                              Status
                            </th>
                            <th
                              style={{ width: '160px' }}
                              className="text-center"
                            >
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {records.map((record, index) => (
                            <tr
                              key={record.id}
                              style={
                                !record.status
                                  ? { opacity: 0.55, background: '#fafafa' }
                                  : undefined
                              }
                            >
                              <td className="text-center">
                                {(page - 1) * limit + index + 1}
                              </td>
                              <td>
                                <Badge
                                  color="light"
                                  className="text-dark border"
                                >
                                  {record.employee?.employee_code || '—'}
                                </Badge>
                              </td>
                              <td>
                                <div className="d-flex align-items-center gap-2">
                                  <User size={14} className="text-muted" />
                                  <span className="fw-semibold">
                                    {record.employee?.name || 'Unknown'}
                                  </span>
                                </div>
                              </td>
                              <td>
                                <div className="d-flex align-items-center gap-1">
                                  <Calendar size={12} className="text-muted" />
                                  <span>{formatDisplayDate(record.date)}</span>
                                </div>
                              </td>
                              <td>
                                <div
                                  style={{
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                    maxWidth: '420px',
                                  }}
                                  title={record.notes}
                                >
                                  {record.notes}
                                </div>
                              </td>
                              <td className="text-center">
                                {record.status ? (
                                  <Badge
                                    style={{
                                      backgroundColor: '#8b0000',
                                      color: '#fff',
                                      border: '1.5px solid #ffd700',
                                      fontSize: '0.65rem',
                                      fontWeight: '800',
                                      padding: '3px 8px',
                                      letterSpacing: '0.04em',
                                    }}
                                  >
                                    ACTIVE
                                  </Badge>
                                ) : (
                                  <Badge color="secondary">REMOVED</Badge>
                                )}
                              </td>
                              <td className="text-center">
                                <div className="d-flex justify-content-center gap-1">
                                  <Button
                                    size="sm"
                                    color="warning"
                                    outline
                                    onClick={() => openEdit(record)}
                                    title="Edit"
                                  >
                                    <Edit2 size={12} />
                                  </Button>
                                  {record.status ? (
                                    <Button
                                      size="sm"
                                      color="danger"
                                      outline
                                      onClick={() => handleToggleStatus(record)}
                                      title="Remove (soft delete)"
                                    >
                                      <Trash2 size={12} />
                                    </Button>
                                  ) : (
                                    <Button
                                      size="sm"
                                      color="success"
                                      outline
                                      onClick={() => handleToggleStatus(record)}
                                      title="Reactivate"
                                    >
                                      <RefreshCw size={12} />
                                    </Button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>

                    {/* ───────────── Pagination ───────────── */}
                    {pagination && pagination.total > 0 && (
                      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mt-3">
                        <div className="d-flex align-items-center gap-2">
                          <small className="text-muted">Rows per page:</small>
                          <Input
                            type="select"
                            bsSize="sm"
                            value={limit}
                            onChange={e => {
                              setLimit(Number(e.target.value));
                              setPage(1);
                            }}
                            style={{ width: '70px' }}
                          >
                            <option value={10}>10</option>
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                          </Input>
                          <small className="text-muted ms-2">
                            Showing{' '}
                            <strong>
                              {(pagination.page - 1) * pagination.limit + 1}
                            </strong>{' '}
                            –{' '}
                            <strong>
                              {Math.min(
                                pagination.page * pagination.limit,
                                pagination.total
                              )}
                            </strong>{' '}
                            of <strong>{pagination.total}</strong>
                          </small>
                        </div>

                        <div className="d-flex align-items-center gap-1">
                          <Button
                            size="sm"
                            color="light"
                            disabled={!pagination.has_prev}
                            onClick={() => goToPage(page - 1)}
                          >
                            <ChevronLeft size={14} />
                          </Button>

                          {Array.from(
                            { length: pagination.total_pages },
                            (_, i) => i + 1
                          )
                            .filter(p => {
                              // Show first, last, current, and ±1 around current
                              if (p === 1 || p === pagination.total_pages)
                                return true;
                              if (Math.abs(p - page) <= 1) return true;
                              return false;
                            })
                            .map((p, idx, arr) => {
                              const prev = arr[idx - 1];
                              const showEllipsis = prev && p - prev > 1;
                              return (
                                <React.Fragment key={p}>
                                  {showEllipsis && (
                                    <span className="text-muted px-1">…</span>
                                  )}
                                  <Button
                                    size="sm"
                                    color={p === page ? 'primary' : 'light'}
                                    onClick={() => goToPage(p)}
                                  >
                                    {p}
                                  </Button>
                                </React.Fragment>
                              );
                            })}

                          <Button
                            size="sm"
                            color="light"
                            disabled={!pagination.has_next}
                            onClick={() => goToPage(page + 1)}
                          >
                            <ChevronRight size={14} />
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* ───────────── Edit Modal ───────────── */}
        <Modal
          isOpen={editModal}
          toggle={() => !saving && setEditModal(false)}
          size="md"
        >
          <ModalHeader toggle={() => !saving && setEditModal(false)}>
            <div className="d-flex align-items-center">
              <Edit2 size={18} className="me-2" />
              <div>
                <div className="fw-semibold">Edit Loss of Pay</div>
                <small className="text-muted">
                  {editing?.employee?.name} •{' '}
                  {editing?.employee?.employee_code}
                </small>
              </div>
            </div>
          </ModalHeader>

          <ModalBody>
            <FormGroup>
              <Label className="form-label-sm">
                Date <span className="text-danger">*</span>
              </Label>
              <Input
                type="date"
                bsSize="sm"
                value={editDate}
                onChange={e => setEditDate(e.target.value)}
                disabled={saving}
              />
              <small className="text-muted">
                Changing the date will move the deduction to that day.
              </small>
            </FormGroup>

            <FormGroup className="mb-0">
              <Label className="form-label-sm">
                Reason / Notes <span className="text-danger">*</span>
              </Label>
              <Input
                type="textarea"
                bsSize="sm"
                rows={4}
                value={editNotes}
                onChange={e => setEditNotes(e.target.value)}
                disabled={saving}
                maxLength={500}
                placeholder="Reason for the Loss of Pay"
              />
              <small className="text-muted">
                {editNotes.length}/500 — attached to the payroll record.
              </small>
            </FormGroup>

            {editError && (
              <Alert color="danger" className="mt-3 mb-0 py-2 small">
                {editError}
              </Alert>
            )}
          </ModalBody>

          <ModalFooter>
            <Button
              color="secondary"
              onClick={() => setEditModal(false)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              color="primary"
              onClick={handleSaveEdit}
              disabled={saving || !editNotes.trim()}
            >
              {saving ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Saving...
                </>
              ) : (
                <>
                  <FileText size={14} className="me-1" />
                  Save Changes
                </>
              )}
            </Button>
          </ModalFooter>
        </Modal>
      </Container>
    </div>
  );
};

export default LossOfPay;